import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Building2, Search } from 'lucide-react';
import { useFetchPartnerPropertiesQuery } from '@@/services/property';
import { openSearchPartnerPropertiesModal } from '@@/features/modal';
import SearchPartnerPropertiesModal from '@@/components/Modals/SearchPartnerPropertiesModal';

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';

const columnHelper = createColumnHelper<Alacaza.Property>();
const columns = [
  columnHelper.accessor((row) => row, {
    header: 'Nom de la résidence',
    cell: (row) => {
      const { name, units } = row.getValue();

      return (
        <div className="py-2 px-3 text-left">
          <p className="font-semibold text-neutral-900 text-body">{name}</p>
          <p className="text-body-sm text-neutral-600 mt-1">{units[0]?.address?.fullAddress}</p>
        </div>
      );
    },
  }),
  columnHelper.accessor('zoneName', {
    header: 'Quartier',
    cell: (row) => {
      const zoneName = row.getValue();

      return (
        <div className="flex justify-center">
          <span className="inline-flex items-center px-3 py-1 rounded-badge text-body-xs font-medium bg-warning-100 text-warning-800">
            {zoneName}
          </span>
        </div>
      );
    },
  }),
  columnHelper.accessor((row) => row, {
    header: 'Actions',
    cell: (row) => {
      const { id } = row.getValue();

      return (
        <div className="flex justify-center">
          <Link
            to={`/properties/${id}`}
            className="bg-neutral-500 hover:bg-neutral-600 px-4 py-2 rounded-button shadow-button transition-colors inline-flex items-center"
          >
            <span className="text-white text-body-sm font-medium">Détails</span>
          </Link>
        </div>
      );
    },
  }),
];

const PropertiesPage: React.FC = () => {
  const { data: properties } = useFetchPartnerPropertiesQuery({
    page: 1,
    limit: 500,
  });

  const dispatch = useDispatch();
  const showSearchModal = useCallback(() => {
    dispatch(openSearchPartnerPropertiesModal());
  }, [dispatch]);

  const table = useReactTable({
    data: properties?.docs || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <SearchPartnerPropertiesModal />
      <div className="px-page-sm py-6 lg:px-page lg:py-8 w-full">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
            <div className="flex flex-row items-center">
              <Building2 size={28} className="text-neutral-900" />
              <h1 className="text-heading-lg text-neutral-900 ml-3">Mon parc</h1>
            </div>
            <button
              className="bg-primary-600 hover:bg-primary-700 px-4 py-2.5 rounded-button shadow-button transition-colors flex items-center space-x-2"
              onClick={showSearchModal}
            >
              <Search size={20} className="text-white" />
              <span className="text-white text-body-sm font-medium">
                Rechercher une résidence
              </span>
            </button>
          </div>

          {/* Mobile Cards - visible < lg */}
          <div className="lg:hidden space-y-4">
            {(properties?.docs || []).map((property) => (
              <div
                key={property.id}
                className="bg-white rounded-card shadow-card border border-neutral-200 p-4 hover:shadow-card-hover transition-shadow"
              >
                <div className="mb-3">
                  <p className="font-semibold text-neutral-900 text-body mb-1">{property.name}</p>
                  <p className="text-body-sm text-neutral-600">{property.units[0]?.address?.fullAddress}</p>
                </div>

                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center px-3 py-1 rounded-badge text-body-xs font-medium bg-warning-100 text-warning-800">
                    {property.zoneName}
                  </span>

                  <Link
                    to={`/properties/${property.id}`}
                    className="bg-neutral-500 hover:bg-neutral-600 px-4 py-2 rounded-button shadow-button transition-colors inline-flex items-center"
                  >
                    <span className="text-white text-body-sm font-medium">Détails</span>
                  </Link>
                </div>
              </div>
            ))}

            {(!properties?.docs || properties.docs.length === 0) && (
              <div className="bg-white rounded-card shadow-card border border-neutral-200 p-8 text-center">
                <p className="text-body text-neutral-500">Aucune résidence trouvée</p>
              </div>
            )}
          </div>

          {/* Desktop Table - visible >= lg */}
          <div className="hidden lg:block bg-white rounded-card shadow-card border border-neutral-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-neutral-200">
              <p className="text-body-sm font-semibold text-neutral-700">
                {properties?.docs?.length || 0} résidence{(properties?.docs?.length || 0) > 1 ? 's' : ''}
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className="px-4 py-3 text-left text-body-sm font-semibold text-white bg-neutral-700 border border-neutral-200"
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="hover:bg-neutral-50 transition-colors">
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-4 py-3 border-b border-neutral-200">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PropertiesPage;
